import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, FlameIcon, RefreshCwIcon } from 'lucide-react';
import { LeadTable } from '../components/HotLeadList/LeadTable';
import { LeadDetailPanel } from '../components/HotLeadList/LeadDetailPanel';
import { useHotLeads } from '../contexts/HotLeadContext';
import { formatRelativeTime } from '../utils/format';
type Tab = 'pending' | 'reviewed' | 'demoted' | 'dnq';
const emptyMessages: Record<Tab, string> = {
  pending: 'Queue is clear — new hot leads land here within about 5 minutes.',
  reviewed: 'Nothing agreed or overridden yet today.',
  demoted: 'No leads have been demoted to nurture.',
  dnq: 'No leads marked does-not-qualify.'
};
interface HotLeadsProps {
  onBack?: () => void;
}
export function HotLeads({
  onBack
}: HotLeadsProps) {
  const {
    pending,
    reviewed,
    demoted,
    dnq,
    urgentCount,
    agreementRate,
    lastSyncedAt,
    isSyncing,
    refresh,
    agree,
    override,
    demote,
    markDnq
  } = useHotLeads();
  const [tab, setTab] = useState<Tab>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(pending[0]?.id ?? null);
  const groups: Record<Tab, typeof pending> = {
    pending,
    reviewed,
    demoted,
    dnq
  };
  const leads = groups[tab];
  const selectedLead = useMemo(() => [...pending, ...reviewed, ...demoted, ...dnq].find(lead => lead.id === selectedId) ?? null, [pending, reviewed, demoted, dnq, selectedId]);
  return <main className="w-full bg-[#f3f3f3] px-6 py-5" data-id="element-186" data-mp="1yx7olanb7tqo">
      <div className="mx-auto max-w-[110rem]" data-id="element-187" data-mp="1yyr8db86cmx7">
        <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-188" data-mp="1ky82a9n9rqxo">
          <div data-id="element-189" data-mp="1l0185mq2674m">
            {onBack && <button type="button" onClick={onBack} className="mb-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-190" data-mp="1pxt0uiyj1x9v">
                <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-191" data-mp="3hwe23zc86ixj" />
                Back to Lead Console
              </button>}
            <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900" data-id="element-192" data-mp="3dwn3j9ggiyig">
              <FlameIcon className="h-5 w-5 text-[#ba0517]" aria-hidden="true" data-id="element-193" data-mp="38vpm4z39u8i0" />
              Hot Lead List
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600" data-id="element-194" data-mp="5eqzqiccy80d">
              Every lead the system graded as hot, with downstream touch history and
              the manager decision logged against it.
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-195" data-mp="3rtjvxfc84o1g">
            <span className="flex items-center gap-1.5" data-id="element-196" data-mp="3mvi8ki7rm2k6">
              <span className={`h-1.5 w-1.5 rounded-full ${isSyncing ? 'bg-amber-500' : 'bg-emerald-500'}`} aria-hidden="true" data-id="element-197" data-mp="3muysz85l7q9b" />
              Live feed · synced {formatRelativeTime(lastSyncedAt.toISOString())}
            </span>
            <button type="button" onClick={refresh} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1.5 font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-198" data-mp="qqy9ydg2sr0d">
              <RefreshCwIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-199" data-mp="15o8knkkw9sv1" />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-200" data-mp="1jebx5jtz287a">
          <Stat label="Pending review" value={String(pending.length)} data-id="element-201" data-mp="1jf3sctjnq3x1" />
          <Stat label="Urgent" value={String(urgentCount)} tone="danger" data-id="element-202" data-mp="1oc3pug2fxycj" />
          <Stat label="Agreement rate" value={`${agreementRate}%`} hint="Autonomy unlocks at 90%" data-id="element-203" data-mp="1td1glh1io0pn" />
          <Stat label="Demoted / DNQ" value={`${demoted.length} / ${dnq.length}`} data-id="element-204" data-mp="1xecxmy6brdm8" />
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]" data-id="element-205" data-mp="2q3wk979ifwrt">
          <section className="overflow-hidden rounded border border-slate-200 bg-white" data-id="element-206" data-mp="2q1siauv697u6">
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 py-2" role="tablist" aria-label="Hot lead views" data-id="element-207" data-mp="33ui3wy6sgaqr">
              <TabButton isActive={tab === 'pending'} onClick={() => setTab('pending')} label="Pending" count={pending.length} data-id="element-208" data-mp="2c682476tv3qg" />
              <TabButton isActive={tab === 'reviewed'} onClick={() => setTab('reviewed')} label="Reviewed" count={reviewed.length} data-id="element-209" data-mp="2l2fnnny26wh1" />
              <TabButton isActive={tab === 'demoted'} onClick={() => setTab('demoted')} label="Demoted" count={demoted.length} data-id="element-210" data-mp="3rkvxwu8xojnz" />
              <TabButton isActive={tab === 'dnq'} onClick={() => setTab('dnq')} label="DNQ" count={dnq.length} data-id="element-211" data-mp="7xu68rcfjhlp" />
            </div>

            <LeadTable leads={leads} selectedId={selectedId} onSelect={setSelectedId} emptyMessage={emptyMessages[tab]} data-id="element-212" data-mp="3v29ykxx6j2p8" />
          </section>

          <div className="min-h-[32rem]" data-id="element-213" data-mp="rmcp6r6udyry">
            <LeadDetailPanel lead={selectedLead} onAgree={agree} onOverride={override} onDemote={demote} onDnq={markDnq} data-id="element-214" data-mp="ro5tw0xwa8xb" />
          </div>
        </div>
      </div>
    </main>;
}
interface StatProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'danger';
}
function Stat({
  label,
  value,
  hint,
  tone = 'default'
}: StatProps) {
  return <div className="rounded border border-slate-200 bg-white px-4 py-3" data-id="element-215" data-mp="1k92vep41idlq">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-216" data-mp="1f9gikgs4izdg">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${tone === 'danger' ? 'text-[#ba0517]' : 'text-slate-900'}`} data-id="element-217" data-mp="1aa3lz4bgadxo">
        {value}
      </p>
      {hint && <p className="text-[11px] text-slate-500" data-id="element-218" data-mp="16b4mw0cehbe5">{hint}</p>}
    </div>;
}
interface TabButtonProps {
  isActive: boolean;
  onClick: () => void;
  label: string;
  count: number;
}
function TabButton({
  isActive,
  onClick,
  label,
  count
}: TabButtonProps) {
  return <button type="button" role="tab" aria-selected={isActive} onClick={onClick} className={`rounded px-3 py-1.5 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] ${isActive ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-300' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-219" data-mp="rkf4oe0iuw0s">
      {label}
      <span className="ml-1.5 text-slate-500" data-id="element-220" data-mp="2p6y2ko3idkwg">{count}</span>
    </button>;
}